import React, { useState, useEffect } from 'react';
import { X, ShoppingCart } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { skuApi } from '../../api';

const ProductSkuDialog = ({ product, onClose, onAddToCart }) => {
  const { language } = useApp();

  const [specs,          setSpecs]          = useState([]);
  const [skus,           setSkus]           = useState([]);
  const [selectedValues, setSelectedValues] = useState({});
  const [selectedSku,    setSelectedSku]    = useState(null);
  const [quantity,       setQuantity]       = useState(1);
  const [loading,        setLoading]        = useState(true);

  useEffect(() => {
    loadSpecs();
  }, [product]);

  const loadSpecs = async () => {
    try {
      const res = await skuApi.getProductSpecs(product.id);
      if (res.success && res.data.has_specs) {
        setSpecs(res.data.specs);
        setSkus(res.data.skus);
      }
    } catch (err) {
      console.error('加载规格失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 选择规格值
  const selectValue = (specId, valueId) => {
    const newSelected = { ...selectedValues, [specId]: valueId };
    setSelectedValues(newSelected);

    // 查找匹配的SKU
    const matched = skus.find(sku =>
      sku.spec_values.every(sv => newSelected[sv.spec_id] === sv.spec_value_id)
    );
    setSelectedSku(matched || null);
  };

  // 判断规格值是否已选
  const isSelected = (specId, valueId) =>
    selectedValues[specId] === valueId;

  // 获取当前SKU价格
  const getPrice = () => {
    if (selectedSku && selectedSku.price != null) {
      return parseFloat(selectedSku.price);
    }
    return parseFloat(product.sale_price || 0);
  };

  // 获取当前SKU库存
  const getStock = () => {
    if (selectedSku) return selectedSku.stock || 0;
    return null;
  };

  // 所有规格是否都已选择
  const allSelected = specs.length > 0 &&
    specs.every(spec => selectedValues[spec.id] != null);

  const handleAddToCart = () => {
    if (!allSelected || !selectedSku) return;

    // 构建SKU标签
    const skuLabel = specs.map(spec => {
      const valueId = selectedValues[spec.id];
      const value   = spec.values.find(v => v.id === valueId);
      return value?.value || '';
    }).join(' / ');

    onAddToCart({
      ...product,
      sku_id:        selectedSku.id,
      sku_label:     skuLabel,
      sku_image:     selectedSku.image || product.image,
      sale_price:    getPrice(),
      stock:         getStock(),
      quantity,
      // 区分有规格商品
      has_sku:       true,
    });
    onClose();
  };

  const getProductName = (p) => {
    if (language === 'en') return p.name_en || p.name;
    if (language === 'km') return p.name_km || p.name;
    return p.name;
  };

  const getSpecName = (spec) => {
    if (language === 'en') return spec.name_en || spec.name;
    if (language === 'km') return spec.name_km || spec.name;
    return spec.name;
  };

  const getValueName = (val) => {
    if (language === 'en') return val.value_en || val.value;
    if (language === 'km') return val.value_km || val.value;
    return val.value;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">

        {/* 头部 */}
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="font-bold text-gray-800 text-lg">选择规格</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* 商品信息 */}
          <div className="flex items-center gap-3">
            {(selectedSku?.image || product.image) && (
              <img
                src={selectedSku?.image || product.image}
                alt={product.name}
                className="w-16 h-16 object-cover rounded-xl border"
              />
            )}
            <div>
              <p className="font-semibold text-gray-800">{getProductName(product)}</p>
              <p className="text-blue-600 font-bold text-lg">${getPrice().toFixed(2)}</p>
              {getStock() !== null && (
                <p className={`text-xs ${getStock() > 0 ? 'text-gray-400' : 'text-red-500'}`}>
                  库存：{getStock()}
                </p>
              )}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* 规格选择 */}
              {specs.map(spec => (
                <div key={spec.id}>
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    {getSpecName(spec)}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {spec.values.map(val => (
                      <button
                        key={val.id}
                        type="button"
                        onClick={() => selectValue(spec.id, val.id)}
                        className={`px-4 py-2 rounded-lg text-sm border transition-colors ${
                          isSelected(spec.id, val.id)
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                        }`}>
                        {getValueName(val)}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              {/* 数量选择 */}
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-700">数量</span>
                <div className="flex items-center gap-2">
                  <button type="button"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-8 h-8 rounded-full border flex items-center justify-center text-gray-600 hover:bg-gray-100">
                    -
                  </button>
                  <span className="w-8 text-center font-medium">{quantity}</span>
                  <button type="button"
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-8 h-8 rounded-full border flex items-center justify-center text-gray-600 hover:bg-gray-100">
                    +
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* 底部按钮 */}
        <div className="p-5 border-t">
          <button
            type="button"
            onClick={handleAddToCart}
            disabled={!allSelected || !selectedSku || getStock() === 0}
            className="w-full py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            {!allSelected
              ? '请选择规格'
              : getStock() === 0
              ? '库存不足'
              : `加入购物车 $${(getPrice() * quantity).toFixed(2)}`
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductSkuDialog;